import React, { useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { isNil } from 'lodash';

import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { MainCard } from '~/components/core/NewDesignSystem/Cards/MainCard/MainCard';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  ConsentDecisionRequest,
  ConsentDecisionResponse,
  ConsentDetails,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { ConsentAnswer } from './ConsentForm';
import ConsentForm from './ConsentForm';
import { TEXTS } from './texts';

// The consent API's refusals: a request it no longer accepts (400), and a user it does not let approve (403).
const REFUSALS: Partial<Record<number, string>> = {
  400: TEXTS.REQUEST_EXPIRED,
  403: TEXTS.STAFF_ONLY,
};

const refusalOf = (status: number | null | undefined): string | undefined =>
  isNil(status) ? undefined : REFUSALS[status];

interface OAuthConsentPageProps {
  /** The route's own location, rather than useLocation(), which the test setup pins to a fixed one. */
  location: RouteComponentProps['location'];
}

/** Where the authorize endpoint sends the user's browser: approve or deny an MCP client's connection to CMS. */
const OAuthConsentPage: React.FC<OAuthConsentPageProps> = ({ location }) => {
  // @ts-expect-error TODO: useOrganization hook needs TypeScript definition
  const { organizationId } = useOrganization();
  // Opaque: the authorize endpoint signed it, and the consent API is handed it back as is.
  const consentRequest = new URLSearchParams(location.search).get('request') ?? '';
  // The caller's own organization: it only meets the route's organization-access check.
  const consentUrl = `/api/v1/organizations/${organizationId}/oauth/consent`;
  const {
    isLoading,
    isError,
    httpStatus,
    data: details,
  } = useDataFetcher<ConsentDetails>(
    consentUrl,
    { params: { request: consentRequest } },
    // Its refusals are shown on the page below, so the global error dialog would only repeat them.
    { shouldReportAxiosError: false }
  );
  const [isDeciding, setIsDeciding] = useState(false);
  const [decisionStatus, setDecisionStatus] = useState<number>();

  const handleDecide = async (answer: ConsentAnswer) => {
    setIsDeciding(true);
    const decision: ConsentDecisionRequest = { ...answer, request: consentRequest };
    try {
      const { data } = await axios.post<ConsentDecisionResponse>(consentUrl, decision);
      // The answer goes to the client at its redirect URI, outside CMS: the buttons stay disabled meanwhile.
      window.location.assign(data.redirect_url);
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (refusalOf(status)) {
        setDecisionStatus(status);
        return;
      }
      reportAxiosError(error);
      setIsDeciding(false);
    }
  };

  // Another site could frame the screen and have the user approve it unseen.
  const isFramed = window.self !== window.top;
  const refusal = isFramed
    ? TEXTS.FRAMED
    : (refusalOf(decisionStatus) ?? (isError ? refusalOf(httpStatus) : undefined));

  return (
    <div className="flex justify-center p-24">
      <MainCard type="elevated" title={TEXTS.TITLE} className="w-full max-w-2xl">
        {refusal ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {refusal}
          </Text>
        ) : (
          <LoadingSwitch isLoading={isLoading} isError={isError}>
            {details ? <ConsentForm details={details} isDeciding={isDeciding} onDecide={handleDecide} /> : null}
          </LoadingSwitch>
        )}
      </MainCard>
    </div>
  );
};

export default OAuthConsentPage;
